import { Box, CircularProgress, Grid } from '@mui/material'
import type { GridRowId, GridRowSelectionModel } from '@mui/x-data-grid'
import { useContext, useEffect, useState } from 'react'
import { CustomTitle } from '../components/CustomTitile'
import { DeviceAddButton } from '../components/DeviceAddButton'
import { DeviceDelButton } from '../components/DeviceDelButton'
import { DeviceImportButton } from '../components/DeviceImportButton'
import { DeviceList } from '../components/DeviceList'
import { DeviceListForKitting } from '../components/DeviceListForKitting'
import { DevicePaper } from '../components/DevicePaper'
import { Footer } from '../components/Footer'
import { ModalConfirmDialog } from '../components/ModalConfirmDialog'
import { SearchField } from '../components/SearchField'
import { loadStatus } from '../constants/loadStatus'
import { errorMsg } from '../constants/messages'
import { useAuth } from '../hooks/useAuth'
import { useC102UserUpdate } from '../hooks/useC102UserUpdate'
import { useC108SalesCompanyUpdate } from '../hooks/useC108SalesCompanyUpdate'
import { useC201DeviceGet } from '../hooks/useC201DeviceGet'
import { useC202DeviceAdd } from '../hooks/useC202DeviceAdd'
import { useC203DeviceDelete } from '../hooks/useC203DeviceDelete'
import { useC205DeviceImport } from '../hooks/useC205DeviceImport'
import { useC901SignedUrlGet } from '../hooks/useC901SignedUrlGet'
import { useCloudStorage } from '../hooks/useCloudStorage'
import type { DeviceInfo } from '../interfaces/DeviceIf'
import { validateImportFileSize } from '../util/FileSize'
import { LanguageContext } from '../util/LanguageContext'
import { VersionContext } from '../util/VersionContext'
import { SideMenu } from './SideMenu'

// デバイス情報管理画面
export const Device = () => {
  // hooks
  const auth = useAuth()
  const c102 = useC102UserUpdate()
  const c108 = useC108SalesCompanyUpdate()
  const c201 = useC201DeviceGet()
  const c202 = useC202DeviceAdd()
  const c203 = useC203DeviceDelete()
  const c205 = useC205DeviceImport()
  const c901 = useC901SignedUrlGet()
  const cs = useCloudStorage()
  // 文字列変換用
  const translation = useContext(LanguageContext)
  const { version } = useContext(VersionContext)
  // ローディングステータス監視
  useEffect(() => {
    if (
      c102.loading === loadStatus.LOADING ||
      c108.loading === loadStatus.LOADING ||
      c201.loading === loadStatus.LOADING ||
      c202.loading === loadStatus.LOADING ||
      c203.loading === loadStatus.LOADING ||
      c205.loading === loadStatus.LOADING ||
      c901.loading === loadStatus.LOADING ||
      cs.loading === loadStatus.LOADING
    ) {
      auth.setIsDisable(true)
    } else {
      auth.setIsDisable(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    c102.loading,
    c108.loading,
    c201.loading,
    c202.loading,
    c203.loading,
    c205.loading,
    c901.loading,
    cs.loading,
  ])
  // ダイアログ
  const [openDialog, setOpenDialog] = useState(false)
  const [errorDialog, setErrorDialog] = useState('')

  // デバイス配列
  const [devices, setDevices] = useState<DeviceInfo[]>([])
  const [selectedDevices, setSelectedDevices] = useState<DeviceInfo[]>([])
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(true)
  const [selectedDeviceId, setDeviceId] = useState('')
  const [isImportDisabled, setIsImportDisabled] = useState(true)
  // const [isExportDisabled, setIsExportDisabled] = useState(true);
  const [gridSelection, setGridSelection] = useState<GridRowId[]>([])

  const clearSelection = () => {
    setGridSelection([])
    setSelectedDevices([])
  }
  // 起動時
  useEffect(() => {
    const fetchData = async () => {
      // デバイス一覧取得
      // ログインユーザのRHQに紐づくデバイス一覧を取得
      const result = await c201.execDeviceGet({ rhq: auth.rhq })
      if (result.success) {
        if (result.info) {
          setDevices(result.info!)
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    }

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // デバイス追加ハンドラ
  const deviceAddHandler = async (
    rhq: string,
    salesCompany: string,
    loginId: string,
    password: string,
    api: string,
    serial: string,
  ) => {
    // デバイス登録
    const result = await c202.execDeviceAdd(rhq, salesCompany, loginId, password, api, serial)
    if (result.success) {
      //console.log("execDeviceAdd.");
      // ログインユーザのRHQに紐づくデバイス一覧を取得
      const result = await c201.execDeviceGet({ rhq: auth.rhq })
      if (result.success) {
        // console.log("execDeviceGet.");
        if (result.info) {
          setDevices(result.info!)
        } else {
          setDevices([])
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
  }

  // デバイス削除ハンドラ
  const deviceDelHandler = async (dummyId: string) => {
    let deleteCounter = 0
    await Promise.all(
      selectedDevices.map(async row => {
        // デバイス削除
        const result = await c203.execDeviceDelete(row.device_id)
        if (result.success) {
          deleteCounter++
        } else {
          setErrorDialog(result.message!)
          setOpenDialog(true)
        }
      }),
    )
    if (deleteCounter > 0) {
      clearSelection()
      // ログインユーザのRHQに紐づくデバイス一覧を取得
      const result = await c201.execDeviceGet({ rhq: auth.rhq })
      if (result.success) {
        if (result.info) {
          setDevices(result.info!)
        } else {
          setDevices([])
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    }
  }

  // デバイス編集ハンドラ
  const deviceEditHandler = async (loginId: string, newEmail: string) => {
    // ユーザー更新（メールアドレス）
    const result = await c102.execUserUpdate(loginId, newEmail)
    if (result.success) {
      // ログインユーザのRHQに紐づくデバイス一覧を取得
      const result = await c201.execDeviceGet({ rhq: auth.rhq })
      if (result.success) {
        if (result.info) {
          setDevices(result.info!)
        } else {
          setDevices([])
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
  }

  //Sales Company編集ハンドラ
  const deviceSalesCompanyEditHandler = async (loginId: string, newSalesCompany: string) => {
    //ユーザー更新（メールアドレス）
    const result = await c108.execSalesCompanyUpdate(loginId, newSalesCompany)
    if (result.success) {
      // ログインユーザのRHQに紐づくデバイス一覧を取得
      const result = await c201.execDeviceGet({ rhq: auth.rhq })
      if (result.success) {
        if (result.info) {
          setDevices(result.info!)
        } else {
          setDevices([])
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
  }

  // デバイス検索ハンドラ
  const deviceSearchHandler = async (loginId: string) => {
    clearSelection()
    // デバイス一覧取得
    const result = await c201.execDeviceGet({ loginId: loginId, rhq: auth.rhq })
    if (result.success) {
      if (result.info) {
        setDevices(result.info!)
      } else {
        setDevices([])
      }
    } else {
      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
  }
  const pattern = /.(zip)$/i
  // 拡張子チェック関数
  const extensionCheck = (file: string) => {
    const result = pattern.test(file)
    return result
  }
  // デバイスインポートハンドラ
  const deviceImportHandler = async (file: File) => {
    // ファイルサイズチェック
    if (!validateImportFileSize(file)) {
      setErrorDialog(errorMsg.IMPORT_FILE_SIZE_ERROR)
      setOpenDialog(true)
      return
    }
    // 拡張子チェック
    if (!extensionCheck(file.name)) {
      setErrorDialog(errorMsg.UNSUPPORTED_FILE_ERROR)
      setOpenDialog(true)
      return
    }
    let deleteCounter = 0
    for (const row of selectedDevices) {
      try {
        // 署名付きURL取得
        const resultUrl = await c901.execSignedUrlGet()
        if (!resultUrl.success) throw new Error(resultUrl.message!)

        const resultCs = await cs.execUploadFile(resultUrl.message!, file)
        if (!resultCs.success) throw new Error(resultCs.message!)

        // デバイスインポート
        const result = await c205.execDeviceImport(row.device_id, resultUrl.message!)
        if (!result.success) throw new Error(result.message!)

        deleteCounter++
      } catch (error) {
        if (error instanceof Error) {
          setErrorDialog(error.message)
          setOpenDialog(true)
          break
        }
      }
    }
    if (deleteCounter > 0) {
      clearSelection()
      // デバイス一覧取得
      // ログインユーザのRHQに紐づくデバイス一覧を取得
      const result = await c201.execDeviceGet({ rhq: auth.rhq })
      if (result.success) {
        if (result.info) {
          setDevices(result.info!)
        } else {
          setDevices([])
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    }
  }

  // リスト選択
  const handleSelectionModelChange = (selectionModel: GridRowSelectionModel) => {
    const selectedIDs = new Set(selectionModel)
    setGridSelection(selectionModel)
    const selectedRowData = devices.filter(row => selectedIDs.has(row.id))
    setSelectedDevices(selectedRowData)
  }

  // 選択デバイスの監視
  useEffect(() => {
    setIsDeleteDisabled(
      selectedDevices.length === 1 && selectedDevices[0].login_id !== auth.loginId ? false : true,
    )
    setDeviceId(selectedDevices.length === 1 ? selectedDevices[0].device_id : '')
    setIsImportDisabled(selectedDevices.length >= 1 && selectedDevices.length <= 20 ? false : true)
    // setIsExportDisabled(
    //   selectedDevices.length >= 1 && selectedDevices.length <= 10 ? false : true
    // );
  }, [selectedDevices, auth.loginId])

  // ダイアログクローズ
  const handleDialogClose = () => {
    setErrorDialog('')
    setOpenDialog(false)
  }

  const hasLineBreak = translation['label_device_info_management']?.includes('\n')

  //多言語化対応
  const userLanguage = navigator.language || navigator.languages[0]
  const baseLanguage = userLanguage.split('-')[0]
  let deviceDeleteButtonPosition: number
  let DeviceImportButtonPosition: number
  let deviceAddButtonPosition: number
  switch (baseLanguage) {
    case 'de':
    case 'sv':
      deviceDeleteButtonPosition = 1137
      DeviceImportButtonPosition = 1344
      deviceAddButtonPosition = 1550
      break
    default:
      deviceDeleteButtonPosition = 1187
      DeviceImportButtonPosition = 1394
      deviceAddButtonPosition = 1600
  }

  return (
    <>
      {openDialog && (
        <ModalConfirmDialog
          open={openDialog}
          text={errorDialog}
          onClick={() => handleDialogClose()}
        />
      )}

      <Box sx={{ display: 'flex', p: 0 }}>
        <Box sx={{ p: 0, width: '338px', flexShrink: 0 }}>
          <SideMenu />
        </Box>
        <Box sx={{ flexGrow: 1, overflowX: 'auto' }} display="flex" flexDirection="column">
          <Box marginTop={13} display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 9999,
              }}
            >
              {(c201.loading === loadStatus.LOADING ||
                c202.loading === loadStatus.LOADING ||
                c203.loading === loadStatus.LOADING ||
                c205.loading === loadStatus.LOADING ||
                c102.loading === loadStatus.LOADING ||
                c108.loading === loadStatus.LOADING ||
                c901.loading === loadStatus.LOADING ||
                cs.loading === loadStatus.LOADING) && <CircularProgress />}
            </Box>

            <Box>
              <CustomTitle>{translation['label_device_info_management']}</CustomTitle>
            </Box>

            <Box>
              <SearchField onKeyDown={deviceSearchHandler} />
            </Box>

            {auth.role === 3 /*3:キッティング業者(RHQ Member/Kitting operator)*/ && (
              <Box
                position="fixed"
                left={deviceAddButtonPosition}
                sx={{
                  '@media (max-width: 1832px)': {
                    left: 'auto',
                    right: '20px',
                  },
                }}
              >
                <DeviceAddButton
                  title={translation['button_title_device']}
                  onAdd={deviceAddHandler}
                >
                  {translation['button_add_new']}
                </DeviceAddButton>
              </Box>
            )}
            {(auth.role === 2 /*2:デバイス管理者(RHQ Administrator)*/ ||
              auth.role === 4) /*4:京セラ社員*/ && (
              <>
                <Box
                  position="fixed"
                  left={deviceDeleteButtonPosition}
                  sx={{
                    '@media (max-width: 1832px)': {
                      left: 'auto',
                      right: '420px',
                    },
                  }}
                >
                  <DeviceDelButton
                    title={translation['button_title_device']}
                    deviceId={selectedDeviceId}
                    isDisabled={isDeleteDisabled}
                    onDel={deviceDelHandler}
                  >
                    {translation['button_delete']}
                  </DeviceDelButton>
                </Box>
                <Box
                  position="fixed"
                  left={DeviceImportButtonPosition}
                  sx={{
                    '@media (max-width: 1832px)': {
                      left: 'auto',
                      right: '220px',
                    },
                  }}
                >
                  <DeviceImportButton
                    title={translation['button_title_device']}
                    isDisabled={isImportDisabled}
                    onImport={deviceImportHandler}
                  >
                    {translation['button_import']}
                  </DeviceImportButton>
                </Box>
                <Box
                  position="fixed"
                  left={deviceAddButtonPosition}
                  sx={{
                    '@media (max-width: 1832px)': {
                      left: 'auto',
                      right: '20px',
                    },
                  }}
                >
                  <DeviceAddButton
                    title={translation['button_title_device']}
                    onAdd={deviceAddHandler}
                  >
                    {translation['button_add_new']}
                  </DeviceAddButton>
                </Box>
              </>
            )}
          </Box>
          <Box
            sx={{
              marginTop: hasLineBreak ? '0px' : '40px',
            }}
          >
            <DevicePaper>
              <Grid container spacing={2}>
                <Grid item lg={6} xl={4} />
                {auth.role === 3 ? (
                  <DeviceListForKitting
                    items={devices}
                    onSalesCompanyEdit={deviceSalesCompanyEditHandler}
                  />
                ) : (
                  <DeviceList
                    items={devices}
                    handleSelectionModelChange={handleSelectionModelChange}
                    onEdit={deviceEditHandler}
                    onSalesCompanyEdit={deviceSalesCompanyEditHandler}
                    gridSelection={gridSelection}
                  />
                )}
              </Grid>
            </DevicePaper>
          </Box>
        </Box>
      </Box>
      <Footer />
    </>
  )
}
